// Brand prefixes for Front / Rear cover codes (stored as "<BRAND> <MODEL#>",
// e.g. "DG 7"). `zh` is the Chinese brand label shown in the dropdown as
// "CODE · 中文" (brand name only — no 前/后 side word). Entries with `zh: null`
// show the code only in both languages (per the list provided by the user).
export const COVER_BRANDS = [
  { code: "AC", zh: "讴歌" },
  { code: "VO", zh: "沃尔沃" },
  { code: "LN", zh: "林肯" },
  { code: "NS", zh: "日产" },
  { code: "JP", zh: "吉普" },
  { code: "PO", zh: "保时捷" },
  { code: "MB", zh: "奔驰" },
  { code: "AD", zh: "奥迪" },
  { code: "TO", zh: "丰田" },
  { code: "LX", zh: "雷克萨斯" },
  { code: "CAD", zh: "凯迪拉克" },
  { code: "IN", zh: "英菲尼迪" },
  { code: "CH", zh: "克莱斯勒" },
  { code: "VW", zh: "大众" },
  { code: "GMC", zh: "GMC" },
  { code: "BMW", zh: "宝马" },
  { code: "LR", zh: "路虎" },
  { code: "KIA", zh: "起亚" },
  { code: "SU", zh: "斯巴鲁" },
  { code: "FD", zh: "福特" },
  { code: "HO", zh: "本田" },
  { code: "BU", zh: "别克" },
  { code: "MZ", zh: "马自达" },
  { code: "DG", zh: "道奇" },
  { code: "TSL", zh: "特斯拉" },
  { code: "MA", zh: null },
  { code: "MT", zh: null },
  { code: "HY", zh: "现代" },
];

export const COVER_BRAND_CODES = COVER_BRANDS.map((b) => b.code);

// Caliper clamp options (Front + Rear). Stored value is the English code;
// 中文 mode shows "code · 中文". "Picture" = see the photo (user rule Oct 6, 2026).
export const CLAMP_OPTIONS = [
  { code: "AL", zh: "铝制" },
  { code: "Double", zh: "双夹" },
  { code: "Single", zh: "单夹" },
  { code: "Fork", zh: "叉形" },
  { code: "Gen 1.", zh: "一代" },
  { code: "Gen 2.", zh: "二代" },
  { code: "Sliding", zh: "滑动式" },
  { code: "Reversed", zh: "反向" },
  { code: "Gen 2. Sliding", zh: "二代滑动式" },
  { code: "Picture", zh: "见图片" },
];
export const CLAMP_CODES = CLAMP_OPTIONS.map((c) => c.code);

export function clampLabel(code, lang) {
  const c = CLAMP_OPTIONS.find((x) => x.code === code);
  if (lang !== "zh" || !c) return code;
  return `${c.code} · ${c.zh}`;
}

// "FF" / "RR" are the existing sentinel codes meaning "no front / no rear cover".
export const SKIP_CODE = { front: "FF", rear: "RR" };
// "NA" in the brand list = this cover is not available → the order moves to NA.
export const NA_CODE = "NA";

// Dropdown label: brand name only (no 前/后 side word), same as the English list.
export function brandLabel(brand, _side, lang) {
  if (lang !== "zh" || !brand.zh) return brand.code;
  return `${brand.code} · ${brand.zh}`;
}

// "DG 7" → { brand: "DG", num: "7" }; "FF" → { brand: "FF", num: "" };
// "丰田前37" → { brand: "丰田前", num: "37" } (legacy prefix kept as-is).
export function splitCoverCode(value) {
  const v = String(value || "").trim();
  if (!v) return { brand: "", num: "" };
  const m = v.match(/^([^\d\s]+)\s*(.*)$/);
  if (!m) return { brand: "", num: v };
  return { brand: m[1], num: m[2].trim() };
}

export function joinCoverCode(brand, num) {
  const b = String(brand || "").trim();
  const n = String(num || "").trim();
  if (b === SKIP_CODE.front || b === SKIP_CODE.rear || b === NA_CODE) return b;
  return [b, n].filter(Boolean).join(" ");
}

// Read-only display: in 中文 mode "DG 7" → "道奇前7" (front) / "HO 44" → "本田后44" (rear).
// Unknown prefixes, MA/MT and FF/RR are shown unchanged.
export function displayCode(value, side, lang) {
  const v = String(value || "").trim();
  if (lang !== "zh" || !v) return v;
  const { brand, num } = splitCoverCode(v);
  const b = COVER_BRANDS.find((x) => x.code === brand.toUpperCase());
  if (!b || !b.zh) return v;
  return `${b.zh}${side === "front" ? "前" : "后"}${num}`;
}

// A cover code is complete when it has a BRAND code + a NUMBER ("DG 7"),
// or is the side's no-cover marker (FF front / RR rear). Mirrors backend `_cover_code_ok`.
export function coverCodeComplete(value, side) {
  const v = String(value || "").trim();
  if (!v) return false;
  if (v.toUpperCase() === SKIP_CODE[side]) return true;
  const { brand, num } = splitCoverCode(v);
  return !!brand && /\d/.test(num);
}

// Returns the list of problems that block Pending → Advance (empty = ready).
export function coverCodeBlockers(codes) {
  const out = [];
  if (!coverCodeComplete(codes.front_cover_code, "front")) out.push("Front cover: brand + number (or FF)");
  if (!coverCodeComplete(codes.rear_cover_code, "rear")) out.push("Rear cover: brand + number (or RR)");
  return out;
}
